import { Component, computed, effect, inject, OnInit, output, signal } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { UsersService } from '@webapp/users/services/users.service';
import { IUserPaymentMethod } from '@webapp/users/interfaces/users.interface';
import { IPayment } from '@webapp/quoter/interfaces/payment.interface';
import { PaymentMethodListComponent, AddCreditCardComponent, AddCbuFormComponent, PaymentHelper, PAYMENT_GATEWAYS } from '@webapp/payments';
import { AddPaymentComponent } from '@webapp/payments/ui/components/add-payment/add-payment.component';
import { AddSecurityCodeComponent } from '@webapp/payments/ui/components/add-security-code/add-security-code.component';
import { ButtonComponent, BackButtonComponent, IconComponent, IconType } from '@webapp/ui';
import { IssuePolicyStore } from '../../../store/issue-policy-store.service';
import { ToastService } from '@webapp/ui/components/toast/toast.service';
import { IPaymentMethod } from '@webapp/payments';
import { CHANNEL, CHANNEL_QUOTE_ENUM } from '@webapp/core/config';

@Component({
  selector: 'app-issue-policy-payment',
  imports: [
    PaymentMethodListComponent,
    AddPaymentComponent,
    ButtonComponent,
    BackButtonComponent,
    AddCreditCardComponent,
    AddCbuFormComponent,
    AddSecurityCodeComponent,
    IconComponent
  ],
  templateUrl: './issue-policy-payment.component.html',
  styleUrl: './issue-policy-payment.component.scss'
})
export class IssuePolicyPaymentComponent implements OnInit {

  constructor() {
    effect(() => this.addingPaymentMethodChange.emit(this.addingPaymentMethod()));
  }

  private readonly usersService = inject(UsersService);
  private readonly store = inject(IssuePolicyStore);
  private readonly toastService = inject(ToastService);
  private readonly paymentHelper = inject(PaymentHelper);
  private readonly channel = inject(CHANNEL, { optional: true });

  // Outputs
  readonly paymentSelected = output<IPayment>();
  readonly addingPaymentMethodChange = output<boolean>();
  
  // State
  readonly userPaymentMethods = signal<IUserPaymentMethod[]>([]);
  readonly paymentMethods = signal<IPaymentMethod[]>([]);
  readonly selectedPaymentId = signal<string | null>(null);
  readonly paymentsLoading = signal(true);
  readonly selectPaymentMethod = signal(false);
  readonly addPaymentMethod = signal(false);
  readonly addCreditCardMethod = signal(false);
  readonly addCbuFormMethod = signal(false);
  readonly showPaymentTypeSelector = signal(false);
  readonly showSecurityCode = signal(false);
  readonly pendingPayment = signal<IPayment | null>(null);

  // Computed
  protected readonly isClientChannel = computed(() => this.channel === CHANNEL_QUOTE_ENUM.DIRECT);
  protected readonly hasMercadoPago = computed(() => this.paymentGateways().includes(PAYMENT_GATEWAYS.MERCADO_PAGO));
  protected readonly user = computed(() => this.store.quote()?.user);
  protected readonly risk = computed(() => this.store.quote()?.risk);
  protected readonly hasUser = computed(() => !!this.user()?.id);
  protected readonly paymentGateways = computed(() => 
    this.risk()?.paymentMethods || []
  );
  protected readonly availablePaymentTypes = computed(() => {
    const gateways = this.paymentGateways();
    const types: Array<{ name: string; type: 'credit_card' | 'cbu' }> = [
      { name: this.hasMercadoPago() ? 'Tarjeta de crédito/débito' : 'Tarjeta de crédito', type: 'credit_card' }
    ];

    if (!this.isClientChannel() && gateways.includes(PAYMENT_GATEWAYS.MERIDIONAL) && !gateways.includes(PAYMENT_GATEWAYS.MERCADO_PAGO)) {
      types.push({ name: 'Débito en cuenta (CBU)', type: 'cbu' });
    }

    return types;
  });

  protected readonly paymentMethodsWithSelection = computed(() => {
    const methods = this.paymentMethods();
    const selectedId = this.selectedPaymentId();

    return methods.map(method => ({
      ...method,
      isSelected: method.id === selectedId
    }));
  });

  readonly addingPaymentMethod = computed(() =>
    this.addPaymentMethod() ||
    this.addCreditCardMethod() ||
    this.addCbuFormMethod() ||
    this.showPaymentTypeSelector() ||
    this.showSecurityCode()
  );

  protected readonly showLoadingState = computed(() =>
    this.paymentsLoading() && !this.addingPaymentMethod()
  );

  ngOnInit(): void {
    if (this.hasUser()) {
      this.getPaymentMethods();
    } else {
      // For guest users, check for MercadoPago options first
      const mercadoPagoOptions = this.paymentHelper.addMercadoPagoOptions([], this.paymentGateways(), this.isClientChannel());
      if (mercadoPagoOptions.length > 0) {
        this.paymentMethods.set(mercadoPagoOptions);
        this.selectPaymentMethod.set(true);
        this.paymentsLoading.set(false);
      } else {
        // No MercadoPago options, show type selector if multiple types available
        const availableTypes = this.availablePaymentTypes();
        if (availableTypes.length > 1) {
          this.showPaymentTypeSelector.set(true);
        } else {
          // Only credit card available, go directly to form
          this.showAddCreditCardMethod();
        }
        this.paymentsLoading.set(false);
      }
    }
  }

  async getPaymentMethods(): Promise<void> {
    const user = this.user();
    if (!user?.id) {
      this.toastService.showToast(
        'Error',
        'Usuario no encontrado',
        'error'
      );
      return;
    }

    try {
      this.paymentsLoading.set(true);
      const response = await firstValueFrom(
        this.usersService.getPaymentMethods(user.id)
      );

      if (response && response.length > 0) {
        this.userPaymentMethods.set(response);
        // Transform to IPaymentMethod for the new components
        const transformedMethods = this.transformUserPaymentMethods(response);
        // CBU is hidden whenever the risk has MercadoPago as a gateway, regardless of channel (agents or client)
        const filteredMethods = this.paymentHelper.filterCbuIfMercadoPago(transformedMethods, this.paymentGateways());
        // Add MercadoPago hardcoded options if available
        const finalMethods = this.paymentHelper.addMercadoPagoOptions(filteredMethods, this.paymentGateways(), this.isClientChannel());
        this.paymentMethods.set(finalMethods);
        this.selectPaymentMethod.set(true);
      } else {
        // No payment methods, but add MercadoPago options if available and show selector
        const mercadoPagoOptions = this.paymentHelper.addMercadoPagoOptions([], this.paymentGateways(), this.isClientChannel());
        if (mercadoPagoOptions.length > 0) {
          this.paymentMethods.set(mercadoPagoOptions);
          this.selectPaymentMethod.set(true);
        } else {
          // No MercadoPago options either, show type selector if multiple types available
          const availableTypes = this.availablePaymentTypes();
          if (availableTypes.length > 1) {
            this.showPaymentTypeSelector.set(true);
          } else {
            // Only credit card available, go directly to form
            this.showAddCreditCardMethod();
          }
        }
      }
    } catch (error) {
      console.error('Error al obtener los medios de pago:', error);
      this.toastService.showToast(
        'Error',
        'Error al cargar los medios de pago',
        'error'
      );
      // No payment methods, but add MercadoPago options if available and show selector
      const mercadoPagoOptions = this.paymentHelper.addMercadoPagoOptions([], this.paymentGateways(), this.isClientChannel());
      if (mercadoPagoOptions.length > 0) {
        this.paymentMethods.set(mercadoPagoOptions);
        this.selectPaymentMethod.set(true);
      } else {
        // No MercadoPago options either, show type selector if multiple types available
        const availableTypes = this.availablePaymentTypes();
        if (availableTypes.length > 1) {
          this.showPaymentTypeSelector.set(true);
        } else {
          // Only credit card available, go directly to form
          this.showAddCreditCardMethod();
        }
      }
    } finally {
      this.paymentsLoading.set(false);
    }
  }

  showAddPaymentMethod(): void {
    this.selectPaymentMethod.set(false);
    this.addPaymentMethod.set(true);
    this.addCreditCardMethod.set(false);
    this.addCbuFormMethod.set(false);
    this.showPaymentTypeSelector.set(false);
  }

  showAddCreditCardMethod(): void {
    this.selectPaymentMethod.set(false);
    this.addPaymentMethod.set(false);
    this.addCreditCardMethod.set(true);
    this.addCbuFormMethod.set(false);
    this.showPaymentTypeSelector.set(false);
  }

  showAddCbuFormMethod(): void {
    this.selectPaymentMethod.set(false);
    this.addPaymentMethod.set(false);
    this.addCreditCardMethod.set(false);
    this.addCbuFormMethod.set(true);
    this.showPaymentTypeSelector.set(false);
  }

  showPaymentMethods(): void {
    this.selectPaymentMethod.set(true);
    this.addPaymentMethod.set(false);
    this.addCreditCardMethod.set(false);
    this.addCbuFormMethod.set(false);
    this.showPaymentTypeSelector.set(false);
  }

  showPaymentTypeSelection(): void {
    this.selectPaymentMethod.set(false);
    this.addPaymentMethod.set(false);
    this.addCreditCardMethod.set(false);
    this.addCbuFormMethod.set(false);
    this.showPaymentTypeSelector.set(true);
  }

  handlePaymentTypeSelection(type: 'credit_card' | 'cbu'): void {
    if (type === 'credit_card') {
      this.showAddCreditCardMethod();
    } else if (type === 'cbu') {
      this.showAddCbuFormMethod();
    }
  }

  setExistingPayment(paymentMethod: IPaymentMethod): void {
    this.pendingPayment.set(null);
    // Update the selected payment ID to show visual selection
    this.selectedPaymentId.set(paymentMethod.id);
    
    // Handle MercadoPago hardcoded options
    if (paymentMethod.id === 'mercado_pago_cash' || paymentMethod.id === 'mercado_pago_link') {
      const payment: IPayment = {
        id: paymentMethod.id,
        type: 'money',
        name: paymentMethod.name,
        exist: false
      };
      this.paymentSelected.emit(payment);
      return;
    }

    // Find the original user payment method for additional data
    const userPaymentMethod = this.userPaymentMethods().find(upm => upm.id === paymentMethod.id);
    
    if (!userPaymentMethod) {
      this.toastService.showToast(
        'Error',
        'Método de pago no encontrado',
        'error'
      );
      return;
    }

    const payment: IPayment = {
      id: paymentMethod.id,
      type: paymentMethod.type,  // Use already transformed type
      name: paymentMethod.name,
      // Map lastFour to number field with masked format
      number: userPaymentMethod.lastFour ? `****${userPaymentMethod.lastFour}` : undefined,
      expiresAt: paymentMethod.expiresAt,
      brand: paymentMethod.brand,  // Include brand information
      exist: true  // Mark as existing payment method
    };

    if (this.needsSecurityCode() && this.isCardPayment(payment.type ?? '')) {
      this.pendingPayment.set(payment);
      this.selectPaymentMethod.set(false);
      this.addPaymentMethod.set(false);
      this.addCreditCardMethod.set(false);
      this.addCbuFormMethod.set(false);
      this.showPaymentTypeSelector.set(false);
      this.showSecurityCode.set(true);
      return;
    }

    this.paymentSelected.emit(payment);
  }

  /**
   * Auto-selects a payment method just created by a logged-in user, once the
   * payment methods list has been refreshed. No-ops (current behavior) when
   * the id is missing or not found in the refreshed list.
   */
  private selectCreatedPaymentMethod(createdId: string | undefined): void {
    if (!createdId) return;

    const match = this.paymentMethods().find(pm => pm.id === createdId);
    if (!match) return;

    this.setExistingPayment(match);
  }

  private isCardPayment(type: string): boolean {
    return type === 'credit_card' || type === 'debit_card';
  }

  handleSecurityCode(code: string): void {
    const payment = this.pendingPayment();
    if (!payment) return;
    this.store.setPaymentData({ ...payment, securityNumber: code });
    this.pendingPayment.set(null);
    this.showSecurityCode.set(false);
    this.showPaymentMethods();
  }

  backFromSecurityCode(): void {
    this.showSecurityCode.set(false);
    this.pendingPayment.set(null);
    this.selectedPaymentId.set(null);
    this.showPaymentMethods();
  }

  private addPaymentMethodForChannel(
    userId: string,
    paymentData: IPayment
  ): Observable<string> {
    return this.channel === CHANNEL_QUOTE_ENUM.DIRECT
      ? this.usersService.addPaymentMethodMe(paymentData)
      : this.usersService.addPaymentMethod(userId, paymentData);
  }

  async handleNewPayment(paymentData: IPayment): Promise<void> {
    const user = this.user();
    if (!user?.id) {
      this.toastService.showToast(
        'Error',
        'Usuario no encontrado',
        'error'
      );
      return;
    }

    try {
      // The API resolves the created payment method's id as a bare string
      // (both addPaymentMethod and addPaymentMethodMe already unwrap
      // `response.data`, which for this endpoint IS the id, not an object).
      const createdId = await firstValueFrom(
        this.addPaymentMethodForChannel(user.id, paymentData)
      );

      this.addPaymentMethod.set(false);
      await this.getPaymentMethods();
      this.selectCreatedPaymentMethod(createdId);
      this.toastService.showToast(
        'Éxito',
        'Medio de pago agregado exitosamente',
        'check_circle'
      );
    } catch (error) {
      console.error('Error al agregar medio de pago:', error);
      this.toastService.showToast(
        'Error',
        'Error al agregar el medio de pago',
        'error'
      );
    }
  }

  async handleCreditCardPayment(paymentData: IPayment): Promise<void> {
    const user = this.user();
    if (!user?.id) {
      // For guest users, proceed directly to payment selection
      this.paymentSelected.emit(paymentData);
      return;
    }

    try {
      const createdId = await firstValueFrom(
        this.addPaymentMethodForChannel(user.id, paymentData)
      );

      this.addCreditCardMethod.set(false);
      await this.getPaymentMethods();
      this.selectCreatedPaymentMethod(createdId);
      this.toastService.showToast(
        'Éxito',
        'Tarjeta agregada exitosamente',
        'check_circle'
      );
    } catch (error) {
      console.error('Error al agregar tarjeta:', error);
      this.toastService.showToast(
        'Error',
        'Error al agregar la tarjeta',
        'error'
      );
    }
  }

  async handleCbuFormPayment(paymentData: IPayment): Promise<void> {
    const user = this.user();
    if (!user?.id) {
      // For guest users, proceed directly to payment selection
      this.paymentSelected.emit(paymentData);
      return;
    }

    try {
      const createdId = await firstValueFrom(
        this.addPaymentMethodForChannel(user.id, paymentData)
      );

      this.addCbuFormMethod.set(false);
      await this.getPaymentMethods();
      this.selectCreatedPaymentMethod(createdId);
      this.toastService.showToast(
        'Éxito',
        'CBU agregado exitosamente',
        'check_circle'
      );
    } catch (error) {
      console.error('Error al agregar CBU:', error);
      this.toastService.showToast(
        'Error',
        'Error al agregar el CBU',
        'error'
      );
    }
  }

  protected needsSecurityCode(): boolean {
    return this.hasMercadoPago();
  }

  private transformUserPaymentMethods(userMethods: IUserPaymentMethod[]): IPaymentMethod[] {
    return userMethods.map(method => ({
      id: method.id || '',
      name: method.name || 'Método de pago',
      type: this.paymentHelper.mapPaymentMethodType(method.type || ''),
      lastFour: method.lastFour?.toString() || '',
      expiresAt: method.expiresAt,
      description: `${method.name} - **** ${method.lastFour}`,
      brand: this.paymentHelper.detectCardBrand(method.name || '', method.paymentMethod || ''),
      isSelected: false,
      isEnabled: true
    }));
  }

  /**
   * Get the icon name for a payment type
   */
  getPaymentTypeIcon(type: string): IconType {
    return this.paymentHelper.getPaymentMethodIcon(type);
  }
}